<template>
  <div>
    <div :class="['auth-content', 'masthead', RouterAuthType.AUTH_SELECT]">
      <div class="gambit-logo">
        <img :src="authLogo" :alt="localize('app.name.full')" />
      </div>
    </div>
    <div v-if="showAuthMethods" class="auth-methods-group">
      <div v-for="methodGroup in authMethodGroups" :key="methodGroup.position" class="methods-row login-link">
        <gam-button v-for="method in methodGroup.items" :key="method.type" v-bind="getLoginButton(method)" />
      </div>
      <div class="auth-context policy"><span v-html="localize('auth.privacy')" /></div>
    </div>

    <OnboardingFlow :visible="showOnboarding" @complete="dismissOnboarding" @skip="dismissOnboarding" />
  </div>
</template>

<script setup lang="ts">
import { RouterAuthType } from '@/core/data/config/uiConfig.interface';
import { gambits, localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import GamButton from '@/views/components/GamButton.vue';
import { useAuth } from '@/views/composables/auth/useAuth';
import type { AuthScreenMethod } from '@/views/composables/auth/useAuth.interface';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import OnboardingFlow from '@/views/main/onboarding/OnboardingFlow.vue';
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

const ONBOARDING_SEEN_KEY = 'gam_onboarding_seen_v2';

// Auth is the only surface that uses the full lockup; everywhere else shows the bare crown.
const authLogo = new URL('../../../ui/images/logo-lockup.svg', import.meta.url).href;

// TODO: re-enable onboarding when ready
const showOnboarding = ref(false);

function dismissOnboarding() {
  showOnboarding.value = false;
  sessionStorage.setItem(ONBOARDING_SEEN_KEY, '1');
}

const { showAuthMethods, authMethodGroups, signInWith } = useAuth({
  serverUrl: gambits.configService.getServiceUrl(),
  authScreenType: RouterAuthType.AUTH_SELECT,
});

const route = useRoute();

onMounted(() => {
  gambits.authService.getFormData();

  if (route.query.redirect) {
    useAuthStore().setRedirectPath(String(route.query.redirect));
  }
});

const getLoginButton = (method: AuthScreenMethod): GamButtonType => {
  return {
    variant: method.isPrimary ? GamButtonVariant.PRIMARY : GamButtonVariant.SECONDARY,
    size: GamButtonSize.LARGE,
    label: !isMobile.value ? method.label : method.mobileLabel,
    isIconOnly: !method.label && !!method.icon,
    leftIcon: method.icon,
    isFullSize: true,
    isCentered: true,
    isDisabled: !method.active,
    isLoading: method.isLoading,
    message: method.errorMessage,
    onClick: () => {
      signInWith(method);
    },
  };
};
</script>

<style scoped lang="scss">
.auth-content {
  .gambit-logo {
    // The lockup is drawn flush to its box at roughly 4:1, so this is the
    // artwork's own width and it lands about 42px tall.
    width: 170px;
    // The card only pads 32px above, which leaves the crown crowded against
    // its top edge now that the lockup carries the masthead on its own.
    margin-top: var(--spacing-l);
    margin-bottom: var(--spacing-l);

    img {
      // Block, so the line box adds no stray descender space below the mark.
      display: block;
      width: 100%;
      height: auto;
    }
  }
}

.masthead {
  /* The methods group below pads 48px and lifts its rows by 16px, which leaves
     48px under the lockup. Pull that back to 36px, a little above the 28px that
     separates the last action from the terms. */
  margin-bottom: -12px;
}

.login-link {
  transform: translateY(calc(-1 * var(--spacing-l)));
}
</style>
